import {
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { Fragment, useContext, useState } from "react";
import {
  FiCheckSquare,
  FiEdit,
  FiPrinter,
  FiSquare,
  FiUser,
} from "react-icons/fi";
import propTypes from "prop-types";

import {
  User,
  api,
  RequestStatus,
  UserType,
  CredentialType,
} from "../../logic/api";
import flex from "../../styles/flex";
import styles from "./styles";
import ErrorDialog from "../notifications/errorDialog/ErrorDialog";
import {
  hasKeyMasterPermissions,
  isAdmin,
  isSysAdmin,
} from "../../utility/utils";
import { SessionContext } from "../../utility/useSession";
import { useLabelPrinter } from "../labels";
import { useLabelTemplates } from "../labels/labelTemplates";
import { useTranslation } from "react-i18next";
import {
  ReleaseFlag,
  useReleaseManagement,
} from "../../utility/feature-management";

const UserListItem: React.FunctionComponent<{
  user: User;
  onEditUser: (user: User) => void;
}> = ({ user: initialUser, onEditUser }) => {
  const [user, setUser] = useState(initialUser);
  const [active, setActive] = useState(user.active === true);
  const session = useContext(SessionContext);
  const setActiveApi = api.hooks.users.setActive();
  const theme = useTheme();
  const isLargeDevice = useMediaQuery(theme.breakpoints.up("md"));
  const { labelPrinter } = useLabelPrinter();
  const { generateUserPrintJob } = useLabelTemplates();
  const { t } = useTranslation();

  const releaseSysAdminUserList = useReleaseManagement(
    ReleaseFlag.releaseHideSysAdminUsersList
  );

  const releaseKeyMasterPermissions = useReleaseManagement(
    ReleaseFlag.releaseKeyMasterPermission
  );

  if (!session.current.site) {
    return null;
  }

  const isCurrentUser = session.current.site.user.id === user.id;
  const isThisUserSysAdmin =
    user.userType === UserType.admin &&
    user.credentialType === CredentialType.systemAdmin;
  const isCurrentUserSysAdmin = isSysAdmin(session);

  const hasKeyMasterRole = hasKeyMasterPermissions(session);
  const isKeyMasterOnly =
    session.current.site.user.keyMaster === true &&
    !isAdmin(session) &&
    !isSysAdmin(session);

  if (
    releaseSysAdminUserList &&
    !isCurrentUserSysAdmin &&
    isThisUserSysAdmin
  ) {
    return null;
  }

  const editable = isCurrentUser || isCurrentUserSysAdmin || (isAdmin(session) && !isThisUserSysAdmin);
  // Admins/SysAdmins and KeyMaster users can toggle active status of any user but not themselves
  const canToggleActiveStatus = !isCurrentUser && hasKeyMasterRole;

  const handleToggleActive = () => {
    setActiveApi.send(
      session.current.context,
      { userId: user.id, active: !active },
      (result) => {
        if (result) {
          setActive(!active);
          setUser((prevUser) => ({ ...prevUser, active: !active }));
        }
      }
    );
  };

  const handlePrintStaffPass = () => {
    labelPrinter.print(generateUserPrintJob(user.location));
  };

  const userRoleLabels: Record<UserType, string> = {
    [UserType.service]: "booking.labels.ingestion",
    [UserType.standard]: "packages.standard",
    [UserType.kiosk]: "users.kiosk",
    [UserType.barrier]: "users.barrier",
    [UserType.mobile]: "booking.labels.phoneMobile",
    [UserType.admin]: "users.administrator",
    [UserType.wmb]: "",
  };

  const userRoleLabel = () => {
    if (isThisUserSysAdmin)
      return <b style={{ color: "red" }}>{t("users.systemAdmin")}</b>;
    return (
      <b style={{ color: user.userType === UserType.admin ? "orange" : "inherit" }}>
        {t(userRoleLabels[user.userType])}
      </b>
    );
  };

  if (!releaseKeyMasterPermissions) {
    return (
      <Fragment>
        <ErrorDialog err={setActiveApi.err} />
        <div style={{ ...styles.listItem, opacity: user.active ? 1 : 0.5 }}>
          <div style={{ ...flex.flexRows, justifyContent: "center", alignItems: "center" }}>
            <div style={flex.flexAuto}>
              <IconButton disabled>
                <FiUser />
              </IconButton>
            </div>
            <div style={{ ...flex.flexFill, textAlign: "left" }}>
              <div><b>{user.firstName} {user.lastName}</b></div>
              <div>{userRoleLabel()}</div>
            </div>

            {isLargeDevice && (
              <div style={{ ...flex.flexFill, textAlign: "left" }}>
                <div>{user.userName}</div>
                <div>{user.email}</div>
              </div>
            )}

            <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
              {editable && (
                <div>
                  <IconButton onClick={() => onEditUser(user)}>
                    <FiEdit />
                  </IconButton>
                  <div><small>{t("general.edit")}</small></div>
                </div>
              )}
            </div>

            <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
              {(isAdmin(session) || isCurrentUser) &&
                user.credentialType !== CredentialType.service && (
                  <div>
                    <IconButton onClick={handlePrintStaffPass}>
                      <FiPrinter />
                    </IconButton>
                    <div><small>{t("users.staffPass")}</small></div>
                  </div>
                )}
            </div>

            {isAdmin(session) && (
              <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
                <div>
                  {setActiveApi.status === RequestStatus.loading ? (
                    <IconButton disabled>
                      <CircularProgress size={22} />
                    </IconButton>
                  ) : (
                    <IconButton disabled={!editable} onClick={handleToggleActive}>
                      {active ? <FiCheckSquare /> : <FiSquare />}
                    </IconButton>
                  )}
                  <div><small>{t("reports.enabled")}</small></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <ErrorDialog err={setActiveApi.err} />
      <div style={{ ...styles.listItem, opacity: user.active ? 1 : 0.5 }}>
        <div style={{ ...flex.flexRows, justifyContent: "center", alignItems: "center" }}>
          <div style={flex.flexAuto}>
            <IconButton disabled>
              <FiUser />
            </IconButton>
          </div>

          <div style={{ ...flex.flexFill, textAlign: "left" }}>
            <div><b>{user.firstName} {user.lastName}</b></div>
            <div>{userRoleLabel()}</div>
          </div>

          {isLargeDevice && (
            <div style={{ ...flex.flexFill, textAlign: "left" }}>
              <div>{user.userName}</div>
              <div>{user.email}</div>
            </div>
          )}

          <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
            {editable && (!isKeyMasterOnly || isCurrentUser) && (
              <div>
                <IconButton onClick={() => onEditUser(user)}>
                  <FiEdit />
                </IconButton>
                <div><small>{t("general.edit")}</small></div>
              </div>
            )}
          </div>

          <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
            {(hasKeyMasterRole || isCurrentUser) &&
              user.credentialType !== CredentialType.service && (
                <div>
                  <IconButton onClick={handlePrintStaffPass}>
                    <FiPrinter />
                  </IconButton>
                  <div><small>{t("users.staffPass")}</small></div>
                </div>
              )}
          </div>

          {(hasKeyMasterRole || isAdmin(session)) && (
            <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
              <div>
                {setActiveApi.status === RequestStatus.loading ? (
                  <IconButton disabled>
                    <CircularProgress size={22} />
                  </IconButton>
                ) : (
                    <IconButton
                      disabled={!canToggleActiveStatus}
                      onClick={handleToggleActive}
                    >
                    {active ? <FiCheckSquare /> : <FiSquare />}
                  </IconButton>
                )}
                <div><small>{t("reports.enabled")}</small></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

UserListItem.propTypes = {
  user: propTypes.any.isRequired,
  onEditUser: propTypes.func.isRequired,
};

export default UserListItem;
